import { useEffect, useState } from "react";

/**
 * Drives the hero entrance by toggling class names on <html>.
 *
 * The animation itself is entirely CSS. This hook decides only *when* each
 * phase starts, and it writes to the document element rather than to a local
 * wrapper because the navigation lives in the shared Layout while the hero
 * lives inside the page — they need a common ancestor to be staged together.
 *
 * Phases:
 *   intro-pending   fonts still loading; everything animated is held hidden
 *   intro-play      the entrance runs
 *   intro-done      finished; the cursor follower may appear
 *
 * If this never runs — JS disabled, a thrown error, reduced motion — no class
 * is added, and because every animation rule in hero.css is scoped to
 * `.intro-play`, the finished hero is what shows. That is the safe direction to
 * fail in: a visitor sees the design, just without the performance.
 */

/** Read once, and keep listening: the setting can change mid-session. */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** How long the whole entrance lasts, read from the generated timeline. */
function introDurationMs(): number {
  if (typeof window === "undefined") return 5600;
  const value = getComputedStyle(document.documentElement).getPropertyValue("--tl-done").trim();
  const seconds = Number.parseFloat(value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5600;
}

export type IntroPhase = "pending" | "playing" | "done";

export function useHeroIntro(): IntroPhase {
  // Derived at first render rather than set from an effect: with reduced motion
  // the hero is already finished, and there is no state transition to make.
  const [phase, setPhase] = useState<IntroPhase>(() =>
    prefersReducedMotion() ? "done" : "pending",
  );

  useEffect(() => {
    const root = document.documentElement;

    // No classes at all: hero.css shows the finished state by default.
    if (prefersReducedMotion()) return;

    root.classList.add("intro-pending");
    let doneTimer: number | undefined;
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      root.classList.remove("intro-pending");
      root.classList.add("intro-play");
      setPhase("playing");

      doneTimer = window.setTimeout(() => {
        if (cancelled) return;
        root.classList.add("intro-done");
        setPhase("done");
      }, introDurationMs());
    };

    /**
     * Wait for fonts before starting.
     *
     * The headline is set in Bodoni Moda at 140px. Starting the type reveal
     * against a fallback face means the line re-flows mid-animation, which
     * reads as a glitch. The 1.2s cap is a guarantee that a slow or blocked
     * font request delays the hero rather than hiding it forever.
     */
    const ready = document.fonts?.ready ?? Promise.resolve();
    const cap = new Promise<void>((resolve) => window.setTimeout(resolve, 1200));
    void Promise.race([ready, cap]).then(start);

    return () => {
      cancelled = true;
      window.clearTimeout(doneTimer);
      root.classList.remove("intro-pending", "intro-play", "intro-done");
    };
  }, []);

  return phase;
}

/**
 * Plays the elevation drawing when it is actually on screen.
 *
 * On desktop the drawing sits beside the headline and starts with everything
 * else. On a phone it is below the fold, so starting it on load would mean the
 * visitor scrolls down to a drawing that already finished — the one part of
 * the design that is worth waiting for, missed entirely.
 */
export function useElevationInView(ref: React.RefObject<HTMLElement | null>): boolean {
  // Both escape hatches are known before the first paint, so they are the
  // initial value rather than an effect that immediately re-renders:
  //   reduced motion            -> show the finished drawing
  //   no IntersectionObserver   -> play it rather than leave a blank panel
  const [play, setPlay] = useState(
    () => prefersReducedMotion() || typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    const node = ref.current;
    if (!node || play) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setPlay(true);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, play]);

  return play;
}
